"use client";

import isEmpty from "lodash/isEmpty";

import { useMemo } from "react";

import { useParams } from "next/navigation";

import {
  DataProvider,
  usePlasmicQueryData,
  usePlasmicCanvasContext,
  useSelector,
} from "@stadium/services/plasmic";
import { getCachedMenuData, getCachedStoreData } from "@server/api";

import { getCountryCodeAndLangFromPathname } from "@stadium/helpers/store";
import { getDynamicSlicesData } from "@helpers/plasmic/store";
import { DATA_PROVIDER_STORE_MENU } from "@helpers/plasmic/slices";

import { DEFAULT_COUNTRY } from "@stadium/helpers/country/constants";
import { MY_WORKSPACE_SLUG } from "@stadium/constants";
import { PROMOTIONAL_STORE_SLUG } from "@constants/env-variables";
import { useWorkspaceContext } from "@contexts/workspace-context";

/*
 * This context seves 2 purposes:
 *
 * 1. Fetch store and menu data ONLY when user is in the Plasmic editor mode.
 *    If user is not in the Plasmic editor mode, data will be fetched in layout and will passed as prop.
 *    REASON: In plasmic editor mode, layout is not rendered.
 *
 * 2. Provide the data to the dynamic slices via DataProvider in plasmic editor mode or on production mode.
 *
 *  To serve first case, we have registered this as Global context provider in plasmic-client.jsx.
 */
export function SwagmagicGlobalContextProvider({
  children,
  countryCode: propCountryCode,
  lang: propLang,
  dynamicSlicesData: propDynamicSlicesData,
}) {
  const params = useParams();

  // isPlasmicEditorMode is true when user is in the Plasmic editor mode.
  const canvasContextObject = usePlasmicCanvasContext();

  /*
   * IMPORTANT: This provider appears twice in production mode:
   * 1. Added in layout.tsx with props (countryCode, lang, dynamicSlicesData) that are fetched server-side
   * 2. Automatically added by Plasmic over PlasmicComponent (because it's registered as a global context provider in plasmic-client.jsx)
   *
   * When the provider is auto-added by Plasmic over PlasmicComponent, we must:
   * - Avoid data fetching (data is already fetched in layout and passed as props)
   * - Avoid overwriting the DataProvider data with key DATA_PROVIDER_STORE_MENU (which is already set by the layout instance)
   *
   * This is handled by:
   * - Only fetching data when isPlasmicEditorMode is true.
   * - Using props when available, or falling back to existing DataProvider data via useSelector,
   *  as we are doing by "const plasmicProviderData = useSelector(DATA_PROVIDER_STORE_MENU);".
   */
  const plasmicProviderData = useSelector(DATA_PROVIDER_STORE_MENU);

  const isPlasmicEditorMode =
    canvasContextObject || (isEmpty(propDynamicSlicesData) && isEmpty(plasmicProviderData));

  const { workspaceSlug } = useWorkspaceContext();

  const { countryCode: paramCode, lang: paramLang } = getCountryCodeAndLangFromPathname(
    params?.countryCode,
  );

  const countryCode = paramCode?.toUpperCase() || propCountryCode || DEFAULT_COUNTRY.iso;
  const lang = paramLang || propLang || "en";

  const slug = PROMOTIONAL_STORE_SLUG;

  // Data will be fetched only when user is in the Plasmic editor mode.
  const { data: menuAndStoreData, isLoading: isMenuAndStoreDataLoading } = usePlasmicQueryData(
    [DATA_PROVIDER_STORE_MENU, slug, countryCode, isPlasmicEditorMode],
    async () => {
      if (!isPlasmicEditorMode || !slug) return { storeData: null, menuData: null };

      const cacheStoreData = await getCachedStoreData(slug);
      const storeData = cacheStoreData?.store;

      if (!storeData) return { storeData: null, menuData: null };

      const menuProps = {
        fullMenu: true,
        store: storeData,
        country: { iso: countryCode },
        isNewShopCreationFlow: false,
      };

      const menuResult = await getCachedMenuData(menuProps);
      const menuData = menuResult?.data;

      return { storeData, menuData };
    },
  );

  const storeData = menuAndStoreData?.storeData;
  const data = menuAndStoreData?.menuData;

  /*
   * dynamicSlicesData is evaluated based on the mode:
   * - In Plasmic editor mode: Computed from data fetched via usePlasmicQueryData above
   * - In production mode: Uses propDynamicSlicesData passed from layout (which was fetched server-side)
   */
  const dynamicSlicesData = useMemo(
    () =>
      isPlasmicEditorMode
        ? getDynamicSlicesData({
            isMenuAndStoreDataLoading,
            store: storeData,
            menuData: data,
            countryIso: countryCode,
          })
        : propDynamicSlicesData,
    [
      isMenuAndStoreDataLoading,
      storeData,
      data,
      countryCode,
      isPlasmicEditorMode,
      propDynamicSlicesData,
    ],
  );

  const value = useMemo(() => {
    if (isPlasmicEditorMode || (countryCode && lang && dynamicSlicesData)) {
      return {
        countryCode,
        lang,
        workspaceSlug: workspaceSlug || MY_WORKSPACE_SLUG,
        dynamicSlicesData,
      };
    }

    return plasmicProviderData;
  }, [
    countryCode,
    lang,
    workspaceSlug,
    dynamicSlicesData,
    isPlasmicEditorMode,
    plasmicProviderData,
  ]);

  return (
    <DataProvider name={DATA_PROVIDER_STORE_MENU} data={value}>
      {children}
    </DataProvider>
  );
}

export default SwagmagicGlobalContextProvider;
